import { useEffect } from 'react';

import { Button } from '../surface.js';
import type { PushForegroundMessage } from './push-client.js';

/**
 * The in-app banner for a push that arrives while the app is OPEN.
 *
 * The service worker only draws a system notification when the app is in the
 * background; in the foreground the message lands in `onMessage` and, before
 * this, rendered on exactly one screen (the queue ticket). This is the
 * app-wide surface for it.
 *
 * Placement: transient, top-anchored, and pushed down by `top` so it stacks
 * UNDER the host's existing top chrome instead of claiming a new corner (the
 * bottom of every app already belongs to tab bars and the update banner). It
 * auto-hides after `autoHideMs`; tapping it opens the deep link.
 */

export interface PushToastProps {
  readonly message: PushForegroundMessage | null;
  /** Title to show when the payload had none (an i18n string from the host). */
  readonly fallbackTitle: string;
  readonly openLabel: string;
  readonly dismissLabel: string;
  readonly onOpen: (deepLink: string) => void;
  readonly onDismiss: () => void;
  /** CSS length for the toast's top edge — below the host's own top chrome. */
  readonly top: string;
  readonly autoHideMs?: number;
}

export function PushToast({
  message,
  fallbackTitle,
  openLabel,
  dismissLabel,
  onOpen,
  onDismiss,
  top,
  autoHideMs = 12000,
}: PushToastProps) {
  useEffect(() => {
    if (!message) return undefined;
    const id = window.setTimeout(onDismiss, autoHideMs);
    return () => window.clearTimeout(id);
  }, [message, onDismiss, autoHideMs]);

  if (!message) return null;
  const title = message.title && message.title.trim() !== '' ? message.title : fallbackTitle;
  const link = message.deepLink;

  return (
    <div
      role="status"
      aria-live="polite"
      className="k-rise"
      style={{
        position: 'fixed',
        top,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 2 * var(--space-4))',
        maxWidth: '26rem',
        zIndex: 60,
        background: 'var(--color-surface)',
        color: 'var(--color-on-surface)',
        border: '1px solid var(--color-border-strong)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-3)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>{title}</p>
        {message.body ? (
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.45,
              overflowWrap: 'anywhere',
            }}
          >
            {message.body}
          </p>
        ) : null}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <Button variant="quiet" onClick={onDismiss}>
          {dismissLabel}
        </Button>
        {link ? (
          <Button
            variant="primary"
            onClick={() => {
              onDismiss();
              onOpen(link);
            }}
          >
            {openLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
